// Turbine physics: power curve with air-density correction, rotor speed, thrust, wakes (Jensen), storm control,
// and the market/grid model that decides curtailment.
import { DEG, interp, clamp } from './util.js';
import { TURB } from './content.js';

export var RHO0 = 1.225;

// IEC 61400-12-1: for pitch-controlled turbines, normalise wind speed with (rho / rho0)^(1/3)
export function powerKW(v, rho) {
  var vn = v * Math.pow((rho || RHO0) / RHO0, 1 / 3);
  if (vn < TURB.cutIn) return 0;
  return Math.min(TURB.ratedKW, interp(TURB.powerCurve, vn));
}

// How a fault changes the power p at hub wind v (shape and size from faults.json, scaled by severity / 0.6):
// shift = power curve moved to the right (pitch, aerodynamics), cap = power limit, partial = loss at partial load,
// uniform = the same share at every wind speed.
export function faultPower(is, v, rho, p) {
  var sh = is && is.f.power; if (!sh || p <= 0) return p;
  var k = is.severity / 0.6;
  if (sh.shape === 'cap') return Math.min(p, sh.kw);
  if (sh.shape === 'shift') { var p0 = powerKW(v, rho); return p0 > 0 ? p * Math.min(1, powerKW(Math.max(0, v - sh.ms * k), rho) / p0) : p; }
  if (sh.shape === 'partial') { var b = sh.loss * k; return p * (1 - (v < 4 || v > 12.5 ? b * 0.2 : b * 1.8 * Math.sin(Math.PI * (v - 4) / 8.5))); }
  return p * (1 - (sh.loss || 0) * k);
}
// share of the annual energy a fault costs, for a Weibull wind climate at hub height (k 2.1, mean about 7.3 m/s)
export function faultLossShare(is) {
  if (!is || !is.f.power) return 0;
  if (is._share != null) return is._share;
  var e0 = 0, e1 = 0, c = 8.2, kw = 2.1;
  for (var v = 0.125; v < 25; v += 0.25) { var w = kw / c * Math.pow(v / c, kw - 1) * Math.exp(-Math.pow(v / c, kw)), p = powerKW(v, RHO0); e0 += w * p; e1 += w * faultPower(is, v, RHO0, p); }
  return (is._share = e0 ? 1 - e1 / e0 : 0);
}
export function thrust(v) { return v < TURB.cutIn ? 0 : interp(TURB.thrustCurve, v); }
export function rotorRpm(v) {
  if (v < TURB.cutIn) return 0;
  var R = TURB.rotorM / 2, rpm = TURB.rotor.tsr * v / R * 60 / (2 * Math.PI);
  return clamp(rpm, TURB.rotor.minRpm, TURB.rotor.maxRpm);
}

// Storm control over a sequence of hourly means and gusts. Returns a share of available power (0..1) per hour and the mode.
export function stormControl(vs, gs, hwrt) {
  var S = TURB.storm, out = [], stopped = false;
  for (var i = 0; i < vs.length; i++) {
    var v = vs[i], g = gs[i], share = 1, mode = 'run';
    if (!hwrt) {
      if (!stopped && (v > S.cutOut || g > S.gustCutOut)) stopped = true;
      else if (stopped && v < S.restart && g < S.restartGust) stopped = false;
      if (stopped) { share = 0; mode = 'storm'; }
    } else {
      if (!stopped && (v > S.hwrtStop || g > S.hwrtGustStop)) stopped = true;
      else if (stopped && v < S.hwrtStop - 3 && g < S.hwrtGustStop - 3) stopped = false;
      if (stopped) { share = 0; mode = 'storm'; }
      else if (v > S.hwrtStart) { share = 1 - (1 - S.hwrtMinShare) * clamp((v - S.hwrtStart) / (S.hwrtStop - S.hwrtStart), 0, 1); mode = 'hwrt'; }
    }
    out.push({ share: share, mode: mode });
  }
  return out;
}

// Farm layout: rows of turbines, 5 rotor diameters apart along the main wind direction, 3.5 across.
export function layout(farm) {
  var D = TURB.rotorM, pos = [], o = farm.orient * DEG, cols = farm.cols, rows = Math.ceil(farm.n / cols);
  for (var k = 0; k < farm.n; k++) {
    var r = Math.floor(k / cols), c = k % cols, x = (c - (cols - 1) / 2) * 3.5 * D + (r % 2 ? 1.75 * D : 0), y = (r - (rows - 1) / 2) * 5 * D;
    pos.push([x * Math.cos(o) + y * Math.sin(o), -x * Math.sin(o) + y * Math.cos(o)]);   // east, north in metres
  }
  return pos;
}

// Jensen wake model: wind speed deficit at each turbine for wind from `dir` (degrees, where the wind comes from)
export function wakeDeficits(pos, dir, ct) {
  var D = TURB.rotorM, k = 0.06, ux = -Math.sin(dir * DEG), uy = -Math.cos(dir * DEG), n = pos.length, out = new Array(n);
  var a = 1 - Math.sqrt(Math.max(0, 1 - ct));
  for (var j = 0; j < n; j++) {
    var s2 = 0;
    for (var i = 0; i < n; i++) {
      if (i === j) continue;
      var dx = pos[j][0] - pos[i][0], dy = pos[j][1] - pos[i][1], along = dx * ux + dy * uy;
      if (along <= D) continue;
      var lat = Math.abs(dx * uy - dy * ux), rw = D / 2 + k * along;
      if (lat < rw) { var d = a * Math.pow(D / (D + 2 * k * along), 2); s2 += d * d; }
    }
    out[j] = Math.min(0.6, Math.sqrt(s2));
  }
  return out;
}

// Modelled day-ahead price (EUR/MWh) from fleet wind, sun and demand; not a market price.
export function modelPrice(cf, sun, hour, weekday, holiday, noise) {
  var dem = hour < 6 ? 0.62 : hour < 8 ? 0.85 : hour < 17 ? 1.0 : hour < 21 ? 1.15 : 0.8;
  if (holiday || weekday === 0 || weekday === 6) dem *= 0.8;
  return Math.max(-120, Math.min(260, 40 + 90 * dem - 120 * cf - 55 * sun + noise));
}
